//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import styled, { css } from 'styled-components';

import { BodySecondaryText, BodyText } from '@components/atoms/text/Text';
import VisuallyHiddenText from '@components/atoms/visually-hidden-text/VisuallyHiddenText';
import { DEFAULT_EMPTY_VALUE } from '@constants/DefaultEmptyValues';
import StarEmptyImage from '@images/star-rating/star-rating-empty.svg';
import StarImage from '@images/star-rating/star-rating-filled.svg';
import StarGrayImage from '@images/star-rating/star-rating-gray.svg';
import { isDefined } from '@utils/filterPredicates';

const DEFAULT_MAX_RATING = 5;

interface StarProps {
  fillPercentage: number;
}

const Star = ({ fillPercentage }: StarProps) => {
  return (
    <StarContainer>
      <EmptyStar />
      {fillPercentage > 0 && (
        <FilledStarWrapper $fillPercentage={fillPercentage}>
          <FilledStar />
        </FilledStarWrapper>
      )}
    </StarContainer>
  );
};

interface StarRatingProps {
  rating?: number;
  shouldShowNumericRating?: boolean;
  maxRating?: number;
  className?: string;
}

/**
 * The StarRating atom displays a visual representation of a rating
 * using stars. It can show progressively filled stars based on the
 * provided rating value, supporting any percentage fill (20%, 70%, etc).
 *
 * When a rating is provided, it displays the appropriate number of stars
 * with precise fill levels and optionally shows the numeric rating value.
 *
 * When no rating is provided, it shows a gray star with a default empty value.
 *
 * The component includes accessibility features with visually hidden text
 * that describes the rating for screen readers.
 *
 * @example
 * // Basic rating display with default settings
 * <StarRating rating={3.7} />
 *
 * // Rating with custom maximum value (out of 10)
 * <StarRating rating={7.5} maxRating={10} />
 *
 * // Rating without numeric display
 * <StarRating rating={4} shouldShowNumericRating={false} />
 *
 * // No rating state
 * <StarRating />
 * ```
 */
export const StarRating = ({
  className,
  maxRating = DEFAULT_MAX_RATING,
  rating,
  shouldShowNumericRating = true,
}: StarRatingProps) => {
  if (!isDefined(rating)) {
    return (
      <StarRatingContainer className={className}>
        <img alt='' src={StarGrayImage} />
        <BodySecondaryText>{DEFAULT_EMPTY_VALUE}</BodySecondaryText>
        <VisuallyHiddenText>No rating</VisuallyHiddenText>
      </StarRatingContainer>
    );
  }

  return (
    <StarRatingContainer className={className}>
      <StarsContainer>
        {Array.from({ length: maxRating }).map((_, index) => {
          const fillAmount = Math.max(0, Math.min(100, (rating - index) * 100));
          return <Star key={index} fillPercentage={fillAmount} />;
        })}
      </StarsContainer>
      <VisuallyHiddenText>
        {`${rating % 1 === 0 ? rating.toString() : rating.toFixed(1)} out of ${maxRating} stars`}
      </VisuallyHiddenText>
      {shouldShowNumericRating && rating > 0 && (
        <BodyText>
          {rating % 1 === 0 ? rating.toString() : rating.toFixed(1)}
        </BodyText>
      )}
    </StarRatingContainer>
  );
};

const StarRatingContainer = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${theme.spacings[1]};
  `,
);

const StarsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StarContainer = styled.div`
  position: relative;
  width: 20px;
  height: 20px;
`;

const BaseStar = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
`;

const EmptyStar = styled(BaseStar)`
  background-image: url(${StarEmptyImage});
`;

const FilledStarWrapper = styled.div<{ $fillPercentage: number }>`
  position: absolute;
  top: 0;
  left: 0;
  width: ${({ $fillPercentage }) => `${$fillPercentage}%`};
  height: 100%;
  overflow: hidden;
`;

const FilledStar = styled(BaseStar)`
  background-image: url(${StarImage});
  width: 20px;
`;
